// The Sources table — Grouped-by-domain (expandable parents) and Flat
// (citation-count desc). The RGS-3 list returns per-page rows; grouping +
// aggregation is entirely client-side (sum citations, element-wise series sum,
// union signals/models, domain-level orbit, max last seen). Multi-page parents
// show no Type pill (per the locked decision — a domain spans kinds); flat rows
// always show Type.

import React from "react";
import type { SourceRow } from "@/types/vb";
import { CompetitorFavicon } from "@/components/vb/CompetitorFavicon";
import { kindLabel, relativeTime } from "./sourcesUtil";
import { SparkLine, OrbitPill, SignalPills, ModelPills } from "./SourceCells";

const PILL_MAX = 2;

function totalCitations(rows: SourceRow[]): number {
  return rows.reduce((a, r) => a + r.citations, 0);
}

/** Element-wise sum of the per-page 6-point series (all aligned to the same
 *  calendar months, so index-by-index is valid). */
function sumSeries(rows: SourceRow[]): number[] {
  const len = rows.reduce((m, r) => Math.max(m, r.series.length), 0);
  const out = new Array(len).fill(0);
  for (const r of rows) r.series.forEach((v, i) => (out[i] += v));
  return out;
}

function union(rows: SourceRow[], key: "signals" | "models"): string[] {
  const seen: string[] = [];
  for (const r of rows) for (const v of r[key]) if (!seen.includes(v)) seen.push(v);
  return seen;
}

/** Domain-level orbit: in our classifier a domain's pages share one orbit, so
 *  take the first non-null. */
function domainOrbit(rows: SourceRow[]): SourceRow["orbit"] {
  return rows.find((r) => r.orbit)?.orbit ?? null;
}

function maxLastSeen(rows: SourceRow[]): string {
  return rows.reduce((m, r) => (r.last_seen > m ? r.last_seen : m), rows[0].last_seen);
}

interface DomainGroup {
  domain: string;
  rows: SourceRow[];
}

function buildGroups(rows: SourceRow[]): DomainGroup[] {
  const map = new Map<string, SourceRow[]>();
  for (const r of rows) {
    const arr = map.get(r.domain);
    if (arr) arr.push(r);
    else map.set(r.domain, [r]);
  }
  const groups = [...map.entries()].map(([domain, rs]) => ({
    domain,
    rows: rs
      .slice()
      .sort((a, b) => b.citations - a.citations || a.display_url.localeCompare(b.display_url)),
  }));
  groups.sort(
    (a, b) => totalCitations(b.rows) - totalCitations(a.rows) || a.domain.localeCompare(b.domain),
  );
  return groups;
}

interface RowProps {
  source: SourceRow;
  child?: boolean;
  onOpen: (id: string) => void;
}

/** A leaf page row (flat, or an expanded child, or a single-page domain). */
function PageRow({ source, child, onOpen }: RowProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      className={`src-row${child ? " src-row--child" : ""}`}
      onClick={() => onOpen(source.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(source.id);
        }
      }}
    >
      <span className="src-cell-src">
        {/* Grouped children sit under the domain's favicon, so they skip their
            own and show just the path (homepage keeps the domain — "/" alone is
            meaningless). Flat / single-page rows show the favicon + full URL. */}
        {!child && (
          <CompetitorFavicon url={source.domain} name={source.domain} size={16} className="src-fav" />
        )}
        {/* The page name is a real link: focus/click opens the cited page in a
            new tab; stopPropagation keeps the row click (which opens the drawer)
            from also firing. */}
        <a
          className="src-dom src-dom-link"
          href={`https://${source.display_url}`}
          target="_blank"
          rel="noopener noreferrer"
          title={source.display_url}
          onClick={(e) => e.stopPropagation()}
        >
          {child
            ? source.path === "/"
              ? source.domain
              : source.path
            : source.domain + (source.path !== "/" ? source.path : "")}
        </a>
      </span>
      <span data-label="Type">
        <span className="src-pill-type">{kindLabel(source.kind)}</span>
      </span>
      <span data-label="Orbit">
        <OrbitPill orbit={source.orbit} />
      </span>
      <span className="src-cell-cites" data-label="Citations">
        <span className="src-cites-n tnum">{source.citations}</span>
        <SparkLine series={source.series} w={56} h={16} />
      </span>
      <span data-label="Signals">
        <SignalPills signals={source.signals} max={PILL_MAX} />
      </span>
      <span data-label="AI models">
        <ModelPills models={source.models} max={PILL_MAX} />
      </span>
      <span className="src-cell-seen" data-label="Last seen">{relativeTime(source.last_seen)}</span>
    </div>
  );
}

interface GroupProps {
  group: DomainGroup;
  open: boolean;
  onToggle: (domain: string) => void;
  onOpen: (id: string) => void;
}

function GroupRows({ group, open, onToggle, onOpen }: GroupProps) {
  // A single-page domain is just its page — no chevron, opens the drawer.
  if (group.rows.length === 1) {
    return <PageRow source={group.rows[0]} onOpen={onOpen} />;
  }
  const cites = totalCitations(group.rows);
  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className="src-row src-row--group"
        aria-expanded={open}
        onClick={() => onToggle(group.domain)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle(group.domain);
          }
        }}
      >
        <span className="src-cell-src">
          <span className={`src-chev${open ? " src-chev--open" : ""}`} aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </span>
          <CompetitorFavicon url={group.domain} name={group.domain} size={16} className="src-fav" />
          <a
            className="src-dom src-dom-link"
            href={`https://${group.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            title={group.domain}
            onClick={(e) => e.stopPropagation()}
          >
            {group.domain}
          </a>
          <span className="src-pages">· {group.rows.length} pages</span>
        </span>
        {/* Multi-page parent: no single Type pill (the domain spans kinds). */}
        <span />
        <span data-label="Orbit">
          <OrbitPill orbit={domainOrbit(group.rows)} />
        </span>
        <span className="src-cell-cites" data-label="Citations">
          <span className="src-cites-n tnum">{cites}</span>
          <SparkLine series={sumSeries(group.rows)} w={56} h={16} />
        </span>
        <span data-label="Signals">
          <SignalPills signals={union(group.rows, "signals")} max={PILL_MAX} />
        </span>
        <span data-label="AI models">
          <ModelPills models={union(group.rows, "models")} max={PILL_MAX} />
        </span>
        <span className="src-cell-seen" data-label="Last seen">{relativeTime(maxLastSeen(group.rows))}</span>
      </div>
      {open && group.rows.map((r) => <PageRow key={r.id} source={r} child onOpen={onOpen} />)}
    </>
  );
}

interface SourcesTableProps {
  rows: SourceRow[];
  grouped: boolean;
  expanded: Record<string, boolean>;
  /** Whether a group with no explicit toggle is open by default (collapsed on
   *  mobile, expanded on desktop). */
  defaultExpanded: boolean;
  onToggleGroup: (domain: string) => void;
  onOpenSource: (id: string) => void;
}

export function SourcesTable({
  rows,
  grouped,
  expanded,
  defaultExpanded,
  onToggleGroup,
  onOpenSource,
}: SourcesTableProps) {
  return (
    <section className="src-table" aria-label="Sources AI cited">
      <div className="src-thead">
        <div>Source</div>
        <div>Type</div>
        <div>Orbit</div>
        <div>Citations</div>
        <div>Signals</div>
        <div>AI models</div>
        <div>Last seen</div>
      </div>
      <div className="src-body">
        {rows.length === 0 ? (
          <div className="src-empty">No sources match these filters.</div>
        ) : grouped ? (
          buildGroups(rows).map((g) => (
            <GroupRows
              key={g.domain}
              group={g}
              open={expanded[g.domain] ?? defaultExpanded}
              onToggle={onToggleGroup}
              onOpen={onOpenSource}
            />
          ))
        ) : (
          rows
            .slice()
            .sort((a, b) => b.citations - a.citations || a.display_url.localeCompare(b.display_url))
            .map((s) => <PageRow key={s.id} source={s} onOpen={onOpenSource} />)
        )}
      </div>
    </section>
  );
}

export default SourcesTable;
